import React, { useEffect, useState } from 'react';
import { Card } from 'reactstrap';
import * as Icon from 'react-feather';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import CallexLoader from '../../../components/CallexLoader';
import Pagination from '../../../components/form/Pagination';
import { checkModulePermission } from '../../../utils/common';
import AdminHeader from '../../../components/header/AdminHeader';
import DeleteConfirmationModal from '../../../components/Modals/DeleteConfirmationModal';

import {
  useCallexAiAgentList,
  callexAiAgentDelete,
  callexAiAgentListGet
} from '../../../store/slices/tenantSlices/configurationSlice';
import { useUserRolePermission } from '../../../store/slices/authSlice';
import { getLanguageLabel, STATUS_OPTIONS } from './AddEditCallexAiAgent';
import TestCallModal from './TestCallModal';
import CallexCallConfigModal from './CallexCallConfigModal';


const CallexAiAgents = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const callexAiAgentList = useCallexAiAgentList();
  const userRolePermission = useUserRolePermission();

  const [isDeleteId, setIsDeleteId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    search: undefined
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [testCallAgent, setTestCallAgent] = useState(null);
  const [isTestCallModalOpen, setIsTestCallModalOpen] = useState(false);
  const [callConfigAgent, setCallConfigAgent] = useState(null);


  const fetchCallexAiAgentList = useDebouncedCallback(async () => {
    setIsLoading(true);
    try {
      const payload = { ...filter };
      await dispatch(callexAiAgentListGet(payload)).unwrap();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  }, 500);

  const deleteCallexAiAgent = async () => {
    setIsDeleting(true);
    try {
      const response = await dispatch(callexAiAgentDelete(isDeleteId)).unwrap();
      if (response) {
        toast.success('Record deleted successfully');
        if (callexAiAgentList?.data?.length === 1) {
          setFilter({ ...filter, page: filter?.page > 1 ? filter.page - 1 : 1 });
        } else {
          fetchCallexAiAgentList();
        }
        setIsDeleteId(null);
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleTestCallModal = (agent = null) => {
    setTestCallAgent(agent);
    setIsTestCallModalOpen(!isTestCallModalOpen);
  };

  useEffect(() => {
    fetchCallexAiAgentList();
  }, [filter]);


  return (
    <div>
      <AdminHeader
        title="AI Agents"
        isExpandSearch
        isAdd
        onAddClick={() => navigate('/ai-callex/ai/callex-ai-agents/new')}
        isAddDisabled={!checkModulePermission('is_add', 'AI Agents', userRolePermission)}
        addBtnName="Add AI Agent"
        searchPlaceholder="Search AI Agents"
        search={filter.search}
        handelSearch={(e) => setFilter({ ...filter, search: e.target.value })}
      />
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 170px)' }}>
          <CallexLoader />
        </Card>
      ) : callexAiAgentList?.data?.length > 0 &&
        checkModulePermission('is_view', 'AI Agents', userRolePermission) ? (
        <Card className="p-0 position-relative m-0">
          <div className="daily-report-table" style={{ height: 'calc(100vh - 210px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Language</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th className="text-center" style={{ width: '140px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {callexAiAgentList?.data?.map((agent) => (
                  <tr key={agent.id}>
                    <td>{agent?.name}</td>
                    <td>{getLanguageLabel(agent?.language)}</td>
                    <td>{agent?.description || '-'}</td>
                    <td>
                      {STATUS_OPTIONS.find((opt) => opt.value === agent.status)?.label ||
                        agent.status}
                    </td>
                    <td>
                      <div className="d-flex justify-content-around align-items-center">
                        <div className="cursor-pointer text-success">
                          <Icon.Phone size={16} onClick={() => toggleTestCallModal(agent)} />
                        </div>
                        <div
                          className={`cursor-pointer text-primary ${
                            !checkModulePermission('is_edit', 'AI Agents', userRolePermission)
                              ? 'callex-disabled'
                              : ''
                          }`}>
                          <Icon.Sliders
                            size={16}
                            onClick={() => {
                              if (
                                checkModulePermission('is_edit', 'AI Agents', userRolePermission)
                              ) {
                                setCallConfigAgent(agent);
                              }
                            }}
                          />
                        </div>
                        <div
                          className={`cursor-pointer text-info ${
                            !checkModulePermission('is_edit', 'AI Agents', userRolePermission)
                              ? 'callex-disabled'
                              : ''
                          }`}>
                          <Icon.Edit
                            size={16}
                            onClick={() => {
                              if (
                                checkModulePermission('is_edit', 'AI Agents', userRolePermission)
                              ) {
                                navigate(`/ai-callex/ai/callex-ai-agents/${agent.id}/edit`);
                              }
                            }}
                          />
                        </div>
                        <div
                          className={`cursor-pointer text-danger ${
                            !checkModulePermission('is_delete', 'AI Agents', userRolePermission)
                              ? 'callex-disabled'
                              : ''
                          }`}>
                          <Icon.Trash
                            size={16}
                            onClick={() => {
                              if (
                                checkModulePermission('is_delete', 'AI Agents', userRolePermission)
                              ) {
                                setIsDeleteId(agent.id);
                              }
                            }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={callexAiAgentList?.total}
              limit={callexAiAgentList?.limit}
              page={callexAiAgentList?.page}
              onLimitChange={(val) => {
                setFilter({ ...filter, page: 1, limit: val });
              }}
              onPageClick={(val) => setFilter({ ...filter, page: val })}
              lastPageClick={(val) => setFilter({ ...filter, page: val })}
              firstPageClick={(val) => setFilter({ ...filter, page: val })}
              onRefreshClick={() => fetchCallexAiAgentList()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 170px)' }}>
          No data available
        </Card>
      )}
      <DeleteConfirmationModal
        isOpen={!!isDeleteId}
        onCloseClick={() => setIsDeleteId(null)}
        onDeleteClick={() => deleteCallexAiAgent()}
        isLoading={isDeleting}
      />

      <TestCallModal
        isOpen={isTestCallModalOpen}
        toggle={() => toggleTestCallModal()}
        agent={testCallAgent}
      />

      <CallexCallConfigModal
        isOpen={!!callConfigAgent}
        toggle={() => setCallConfigAgent(null)}
        agent={callConfigAgent}
        onSaved={fetchCallexAiAgentList}
      />
    </div>

  );
};

export default CallexAiAgents;
